// G-Code generation utilities for CNC machines

// Controller configurations (Inch mode - G20)
export const controllerConfig = {
  fanuc: {
    name: 'FANUC',
    programFormat: (num, name) => `O${String(num).padStart(4, '0')}${name ? ` (${name})` : ''}`,
    comment: (text) => `(${text})`,
    toolCall: (tool, offset) => `T${String(tool).padStart(2, '0')}${offset ? String(offset).padStart(2, '0') : String(tool).padStart(2, '0')} M06`,
    spindleOn: (mode, speed) => `${mode} S${speed} M03`,
    spindleOff: 'M05',
    coolantOn: 'M08',
    coolantOff: 'M09',
    feedPerRev: 'G99',
    feedPerMin: 'G98',
    homeReturn: 'G28 U0 W0',
    programEnd: 'M30',
    safeStart: ['G18', 'G40 G80', 'G20'],
    rapid: 'G00',
    linear: 'G01',
    cwArc: 'G02',
    ccwArc: 'G03',
    cycles: {
      roughLong: 'G71',
      roughFace: 'G72',
      finish: 'G70',
      threading: 'G76',
      peckGroove: 'G74',
      peckFaceGroove: 'G75',
      drill: 'G81',
      peck: 'G83',
      tap: 'G84',
      bore: 'G85'
    }
  },
  siemens: {
    name: 'Siemens SINUMERIK',
    programFormat: (num, name) => `; Program ${num}${name ? ` - ${name}` : ''}`,
    comment: (text) => `; ${text}`,
    toolCall: (tool, offset) => `T${tool} D${offset || 1}\nM6`,
    spindleOn: (mode, speed) => mode === 'G96' ? `G96 S${speed} LIMS=${speed * 3}` : `G97 S${speed}`,
    spindleOff: 'M5',
    coolantOn: 'M8',
    coolantOff: 'M9',
    feedPerRev: 'G95',
    feedPerMin: 'G94',
    homeReturn: 'G30',
    programEnd: 'M30',
    safeStart: ['G18', 'G40 G60', 'G70'],
    rapid: 'G0',
    linear: 'G1',
    cwArc: 'G2',
    ccwArc: 'G3',
    cycles: {
      roughLong: 'CYCLE95',
      roughFace: 'CYCLE95',
      finish: 'CYCLE95',
      threading: 'CYCLE97',
      peckGroove: 'CYCLE93',
      peckFaceGroove: 'CYCLE93',
      drill: 'CYCLE81',
      peck: 'CYCLE83',
      tap: 'CYCLE84',
      bore: 'CYCLE85'
    }
  },
  haas: {
    name: 'Haas',
    programFormat: (num, name) => `O${String(num).padStart(5, '0')}${name ? ` (${name})` : ''}`,
    comment: (text) => `(${text})`,
    toolCall: (tool, offset) => `T${tool} M06`,
    spindleOn: (mode, speed) => `${mode} S${speed} M03`,
    spindleOff: 'M05',
    coolantOn: 'M08',
    coolantOff: 'M09',
    feedPerRev: 'G99',
    feedPerMin: 'G98',
    homeReturn: 'G28 G91 Z0\nG28 X0 Y0',
    programEnd: 'M30',
    safeStart: ['G17 G40 G80', 'G20', 'G90'],
    rapid: 'G00',
    linear: 'G01',
    cwArc: 'G02',
    ccwArc: 'G03',
    cycles: {
      roughLong: 'G71',
      roughFace: 'G72',
      finish: 'G70',
      threading: 'G76',
      peckGroove: 'G74',
      peckFaceGroove: 'G75',
      drill: 'G81',
      peck: 'G83',
      tap: 'G84',
      bore: 'G85'
    }
  },
  heidenhain: {
    name: 'Heidenhain',
    programFormat: (num, name) => `BEGIN PGM ${num}${name ? ` ${name}` : ''} INCH`,
    comment: (text) => `; ${text}`,
    toolCall: (tool, offset) => `TOOL CALL ${tool} Z S1000`,
    spindleOn: (mode, speed) => `L M3 S${speed}`,
    spindleOff: 'L M5',
    coolantOn: 'L M8',
    coolantOff: 'L M9',
    feedPerRev: '; Feed per rev',
    feedPerMin: '; Feed per min',
    homeReturn: 'L Z+4.0 R0 FMAX',
    programEnd: 'END PGM',
    safeStart: ['; Heidenhain ISO Mode - INCH'],
    rapid: 'L',
    linear: 'L',
    cwArc: 'CC',
    ccwArc: 'CC',
    cycles: {
      drill: 'CYCL DEF 200',
      peck: 'CYCL DEF 203',
      tap: 'CYCL DEF 206',
      bore: 'CYCL DEF 201'
    }
  },
  okuma: {
    name: 'Okuma OSP',
    programFormat: (num, name) => `O${String(num).padStart(4, '0')}${name ? ` (${name})` : ''}`,
    comment: (text) => `(${text})`,
    toolCall: (tool, offset) => `T${String(tool).padStart(2, '0')}${String(offset || tool).padStart(2, '0')}`,
    spindleOn: (mode, speed) => `${mode} S${speed} M03`,
    spindleOff: 'M05',
    coolantOn: 'M08',
    coolantOff: 'M09',
    feedPerRev: 'G99',
    feedPerMin: 'G98',
    homeReturn: 'G28 U0 W0',
    programEnd: 'M30',
    safeStart: ['G18', 'G40 G80', 'G20'],
    rapid: 'G00',
    linear: 'G01',
    cwArc: 'G02',
    ccwArc: 'G03',
    cycles: {
      roughLong: 'G71',
      roughFace: 'G72',
      finish: 'G70',
      threading: 'G76',
      peckGroove: 'G74',
      peckFaceGroove: 'G75',
      drill: 'G81',
      peck: 'G83',
      tap: 'G84',
      bore: 'G85'
    }
  },
  fadal: {
    name: 'Fadal',
    programFormat: (num, name) => `O${String(num).padStart(4, '0')}${name ? ` (${name})` : ''}`,
    comment: (text) => `(${text})`,
    toolCall: (tool, offset) => `T${tool} M06`,
    spindleOn: (mode, speed) => `S${speed} M03`,
    spindleOff: 'M05',
    coolantOn: 'M08',
    coolantOff: 'M09',
    feedPerRev: 'G99',
    feedPerMin: 'G98',
    homeReturn: 'G28 G91 Z0',
    programEnd: 'M30',
    safeStart: ['G17 G40 G80', 'G20', 'G90'],
    rapid: 'G00',
    linear: 'G01',
    cwArc: 'G02',
    ccwArc: 'G03',
    cycles: {
      drill: 'G81',
      peck: 'G83',
      tap: 'G84',
      bore: 'G85'
    }
  },
  mazak: {
    name: 'Mazak Mazatrol',
    programFormat: (num, name) => `O${String(num).padStart(4, '0')}${name ? ` (${name})` : ''}`,
    comment: (text) => `(${text})`,
    toolCall: (tool, offset) => `T${String(tool).padStart(2, '0')}${String(offset || tool).padStart(2, '0')} M06`,
    spindleOn: (mode, speed) => `${mode} S${speed} M03`,
    spindleOff: 'M05',
    coolantOn: 'M08',
    coolantOff: 'M09',
    feedPerRev: 'G99',
    feedPerMin: 'G98',
    homeReturn: 'G28 U0 W0',
    programEnd: 'M30',
    safeStart: ['G18', 'G40 G80', 'G20'],
    rapid: 'G00',
    linear: 'G01',
    cwArc: 'G02',
    ccwArc: 'G03',
    cycles: {
      roughLong: 'G71',
      roughFace: 'G72',
      finish: 'G70',
      threading: 'G76',
      peckGroove: 'G74',
      peckFaceGroove: 'G75',
      drill: 'G81',
      peck: 'G83',
      tap: 'G84',
      bore: 'G85'
    }
  },
  doosan: {
    name: 'Doosan',
    programFormat: (num, name) => `O${String(num).padStart(4, '0')}${name ? ` (${name})` : ''}`,
    comment: (text) => `(${text})`,
    toolCall: (tool, offset) => `T${String(tool).padStart(2, '0')}${String(offset || tool).padStart(2, '0')}`,
    spindleOn: (mode, speed) => `${mode} S${speed} M03`,
    spindleOff: 'M05',
    coolantOn: 'M08',
    coolantOff: 'M09',
    feedPerRev: 'G99',
    feedPerMin: 'G98',
    homeReturn: 'G28 U0 W0',
    programEnd: 'M30',
    safeStart: ['G18', 'G40 G80', 'G20'],
    rapid: 'G00',
    linear: 'G01',
    cwArc: 'G02',
    ccwArc: 'G03',
    cycles: {
      roughLong: 'G71',
      roughFace: 'G72',
      finish: 'G70',
      threading: 'G76',
      peckGroove: 'G74',
      peckFaceGroove: 'G75',
      drill: 'G81',
      peck: 'G83',
      tap: 'G84',
      bore: 'G85'
    }
  }
};

// Controllers that support FANUC-style canned cycles (G71, G72, G75, G76, G74)
const fanucCycleControllers = ['fanuc', 'haas', 'okuma', 'mazak', 'doosan'];
const supportsFanucCycles = (ctrl) => fanucCycleControllers.includes(ctrl);

// Material presets (Imperial - SFM and IPR)
export const materialPresets = {
  'alu-6061': { name: 'Aluminum 6061-T6', sfm: '300-500', feed: '0.006-0.012 IPR', doc: 'Up to 0.120"' },
  'alu-7075': { name: 'Aluminum 7075-T6', sfm: '250-400', feed: '0.004-0.010 IPR', doc: 'Up to 0.100"' },
  'steel-mild': { name: 'Mild Steel', sfm: '80-120', feed: '0.003-0.008 IPR', doc: 'Up to 0.080"' },
  'steel-4140': { name: 'Alloy Steel 4140', sfm: '60-100', feed: '0.002-0.006 IPR', doc: 'Up to 0.060"' },
  'ss-304': { name: 'Stainless 304', sfm: '40-80', feed: '0.002-0.005 IPR', doc: 'Up to 0.040"' },
  'ss-316': { name: 'Stainless 316', sfm: '35-70', feed: '0.002-0.004 IPR', doc: 'Up to 0.030"' },
  'brass': { name: 'Brass', sfm: '200-400', feed: '0.004-0.010 IPR', doc: 'Up to 0.120"' },
  'titanium': { name: 'Titanium Ti-6Al-4V', sfm: '30-60', feed: '0.001-0.003 IPR', doc: 'Up to 0.020"' },
  'plastic-delrin': { name: 'Delrin/POM', sfm: '400-800', feed: '0.006-0.016 IPR', doc: 'Up to 0.150"' },
};

// Turning operation definitions (Imperial - inches)
export const turningOperations = {
  'facing': {
    name: 'Facing',
    cycle: 'G72/CYCLE95',
    description: 'Face the end of the workpiece',
    fields: [
      { id: 'stockDia', label: 'Stock Diameter (in)', default: 2.0 },
      { id: 'finishDia', label: 'Finish Diameter (in)', default: 0 },
      { id: 'faceLength', label: 'Face Amount (in)', default: 0.080 },
    ]
  },
  'od-roughing': {
    name: 'OD Roughing',
    cycle: 'G71/CYCLE95',
    description: 'Rough turn outside diameter',
    fields: [
      { id: 'stockDia', label: 'Stock Diameter (in)', default: 2.0 },
      { id: 'finishDia', label: 'Finish Diameter (in)', default: 1.5 },
      { id: 'length', label: 'Length (in)', default: 2.0 },
      { id: 'doc', label: 'Depth of Cut (in)', default: 0.080 },
    ]
  },
  'od-finishing': {
    name: 'OD Finishing',
    cycle: 'G70/CYCLE95',
    description: 'Finish turn outside diameter',
    fields: [
      { id: 'finishDia', label: 'Finish Diameter (in)', default: 1.5 },
      { id: 'length', label: 'Length (in)', default: 2.0 },
    ]
  },
  'od-threading': {
    name: 'OD Threading',
    cycle: 'LONG-HAND',
    description: 'Cut external threads (multi-pass long-hand)',
    fields: [
      { id: 'threadSystem', label: 'Thread System', type: 'select', options: ['imperial', 'metric'], default: 'imperial' },
      { id: 'threadSeries', label: 'Thread Series', type: 'select', options: ['UNC', 'UNF', 'coarse', 'fine'], default: 'UNC' },
      { id: 'threadDesignation', label: 'Thread Size', type: 'threadSelect', default: '1/2-13' },
      { id: 'majorDia', label: 'Major Diameter (in)', default: 0.500 },
      { id: 'minorDia', label: 'Minor Diameter (in)', default: 0.4084 },
      { id: 'pitch', label: 'Pitch (in/rev)', default: 0.07692 },
      { id: 'tpi', label: 'TPI', default: 13 },
      { id: 'length', label: 'Thread Length (in)', default: 1.0 },
      { id: 'threadMethod', label: 'Infeed Method', type: 'select', options: ['straight', 'compound'], default: 'compound' },
      { id: 'springPasses', label: 'Spring Passes', default: 2 },
      { id: 'chamferEntry', label: 'Chamfer Entry (threads)', default: 1 },
    ]
  },
  'id-boring': {
    name: 'ID Boring',
    cycle: 'G71/CYCLE95',
    description: 'Bore inside diameter',
    fields: [
      { id: 'startDia', label: 'Start Bore Dia (in)', default: 0.750 },
      { id: 'finishDia', label: 'Finish Bore Dia (in)', default: 1.0 },
      { id: 'depth', label: 'Bore Depth (in)', default: 1.25 },
    ]
  },
  'grooving': {
    name: 'Grooving',
    cycle: 'G75/CYCLE93',
    description: 'Cut OD groove',
    fields: [
      { id: 'grooveDia', label: 'Groove Diameter (in)', default: 1.375 },
      { id: 'grooveWidth', label: 'Groove Width (in)', default: 0.125 },
      { id: 'zPosition', label: 'Z Position (in)', default: -0.750 },
    ]
  },
  'parting': {
    name: 'Parting',
    cycle: 'G75/CYCLE93',
    description: 'Part off workpiece',
    fields: [
      { id: 'partDia', label: 'Part to Diameter (in)', default: 0 },
      { id: 'zPosition', label: 'Z Position (in)', default: -2.0 },
      { id: 'toolWidth', label: 'Tool Width (in)', default: 0.125 },
    ]
  },
  'bar-puller': {
    name: 'Bar Puller',
    cycle: 'M11/M10',
    description: 'Pull bar stock for multi-part production (1" Square Shank Heavy Duty)',
    fields: [
      { id: 'pullLength', label: 'Pull Length (in)', default: 1.0 },
      { id: 'safeX', label: 'Safe X Position (in)', default: 4.0 },
      { id: 'approachFeed', label: 'Approach Feed (IPM)', default: 50.0 },
      { id: 'pullFeed', label: 'Pull Feed (IPM)', default: 20.0 },
      { id: 'dwellTime', label: 'Dwell Time (sec)', default: 1.0 },
      { id: 'gripRange', label: 'Grip Range', default: 'standard', options: [
        { value: 'standard', label: 'Standard Jaw (1/8" - 2-1/4")' },
        { value: 'extra', label: 'Extra Capacity (1-7/8" - 3-3/4")' }
      ]},
    ]
  },
  'center-drill': {
    name: 'Center Drill',
    cycle: 'G81/CYCLE81',
    description: 'Spot drill to center hole before drilling',
    fields: [
      { id: 'drillDia', label: 'Spot Drill Diameter (in)', default: 0.250 },
      { id: 'depth', label: 'Spot Depth (in)', default: 0.150 },
      { id: 'spotAngle', label: 'Spot Angle (deg)', default: 90 },
    ]
  },
  'drilling': {
    name: 'Drilling (Pilot/Through)',
    cycle: 'G83/CYCLE83',
    description: 'Drill hole - use after center drill for bore prep',
    fields: [
      { id: 'drillDia', label: 'Drill Diameter (in)', default: 0.375 },
      { id: 'depth', label: 'Depth (in)', default: 1.25 },
      { id: 'peck', label: 'Peck Depth (in)', default: 0.200 },
      { id: 'drillType', label: 'Drill Type', default: 'pilot', options: [
        { value: 'pilot', label: 'Pilot Drill (for boring)' },
        { value: 'through', label: 'Through Drill' },
        { value: 'blind', label: 'Blind Hole' }
      ]},
    ]
  },
  'taper': {
    name: 'Taper Turning',
    cycle: 'G71/CYCLE95',
    description: 'Turn tapered surface',
    fields: [
      { id: 'startDia', label: 'Start Diameter (in)', default: 2.0 },
      { id: 'endDia', label: 'End Diameter (in)', default: 1.5 },
      { id: 'length', label: 'Taper Length (in)', default: 1.25 },
    ]
  },
  'pattern-repeat': {
    name: 'G73 Pattern Repeat (Casting/Forging)',
    cycle: 'G73',
    description: 'Rough near-net-shape stock where rough profile follows finish profile (castings, forgings)',
    fields: [
      { id: 'finishDia', label: 'Finish Diameter (in)', default: 1.5 },
      { id: 'length', label: 'Length (in)', default: 2.0 },
      { id: 'stockAllowance', label: 'Stock Allowance (in)', default: 0.120 },
      { id: 'doc', label: 'Depth per Pass (in)', default: 0.040 },
      { id: 'finishAllowance', label: 'Finish Allowance (in)', default: 0.015 },
      { id: 'profileType', label: 'Profile', type: 'select', options: ['straight', 'taper', 'contour'], default: 'straight' },
      { id: 'endDia', label: 'End Dia (taper only)', default: 1.0 },
    ]
  },
};

// Milling operation definitions (Imperial - inches)
export const millingOperations = {
  'face-mill': {
    name: 'Face Mill',
    description: 'Face mill the top surface',
    fields: [
      { id: 'width', label: 'Width X (in)', default: 4.0 },
      { id: 'length', label: 'Length Y (in)', default: 4.0 },
      { id: 'depth', label: 'Depth (in)', default: 0.080 },
      { id: 'toolDia', label: 'Tool Diameter (in)', default: 2.0 },
      { id: 'stepover', label: 'Stepover %', default: 70 },
    ]
  },
  'pocket-rect': {
    name: 'Rectangular Pocket',
    description: 'Mill rectangular pocket',
    fields: [
      { id: 'width', label: 'Width X (in)', default: 2.0 },
      { id: 'length', label: 'Length Y (in)', default: 1.25 },
      { id: 'depth', label: 'Depth (in)', default: 0.375 },
      { id: 'toolDia', label: 'Tool Diameter (in)', default: 0.375 },
      { id: 'stepdown', label: 'Step Down (in)', default: 0.080 },
      { id: 'cornerR', label: 'Corner Radius (in)', default: 0.1875 },
    ]
  },
  'pocket-circ': {
    name: 'Circular Pocket',
    description: 'Mill circular pocket',
    fields: [
      { id: 'diameter', label: 'Pocket Diameter (in)', default: 1.5 },
      { id: 'depth', label: 'Depth (in)', default: 0.375 },
      { id: 'toolDia', label: 'Tool Diameter (in)', default: 0.375 },
      { id: 'stepdown', label: 'Step Down (in)', default: 0.080 },
    ]
  },
  'drill': {
    name: 'Drill',
    description: 'Drill hole',
    fields: [
      { id: 'x', label: 'X Position (in)', default: 0 },
      { id: 'y', label: 'Y Position (in)', default: 0 },
      { id: 'depth', label: 'Depth (in)', default: 0.750 },
      { id: 'retract', label: 'Retract R (in)', default: 0.100 },
    ]
  },
  'peck-drill': {
    name: 'Peck Drill',
    description: 'Peck drill deep hole',
    fields: [
      { id: 'x', label: 'X Position (in)', default: 0 },
      { id: 'y', label: 'Y Position (in)', default: 0 },
      { id: 'depth', label: 'Depth (in)', default: 1.25 },
      { id: 'peck', label: 'Peck Q (in)', default: 0.200 },
      { id: 'retract', label: 'Retract R (in)', default: 0.100 },
    ]
  },
  'tap': {
    name: 'Tapping',
    description: 'Tap threaded hole',
    fields: [
      { id: 'x', label: 'X Position (in)', default: 0 },
      { id: 'y', label: 'Y Position (in)', default: 0 },
      { id: 'depth', label: 'Depth (in)', default: 0.625 },
      { id: 'pitch', label: 'Pitch (TPI)', default: 20 },
    ]
  },
  'contour': {
    name: 'Contour',
    description: 'Contour milling',
    fields: [
      { id: 'depth', label: 'Depth (in)', default: 0.375 },
      { id: 'stepdown', label: 'Step Down (in)', default: 0.080 },
      { id: 'toolDia', label: 'Tool Diameter (in)', default: 0.375 },
      { id: 'offset', label: 'Offset (in)', default: 0 },
    ]
  },
  'slot': {
    name: 'Slot',
    description: 'Mill slot',
    fields: [
      { id: 'length', label: 'Slot Length (in)', default: 2.0 },
      { id: 'width', label: 'Slot Width (in)', default: 0.3125 },
      { id: 'depth', label: 'Depth (in)', default: 0.200 },
      { id: 'toolDia', label: 'Tool Diameter (in)', default: 0.3125 },
    ]
  },
  'helical-bore': {
    name: 'Helical Bore',
    description: 'Helical interpolation bore',
    fields: [
      { id: 'diameter', label: 'Bore Diameter (in)', default: 0.750 },
      { id: 'depth', label: 'Depth (in)', default: 0.625 },
      { id: 'toolDia', label: 'Tool Diameter (in)', default: 0.375 },
      { id: 'helixPitch', label: 'Helix Pitch (in)', default: 0.080 },
    ]
  },
  // Javatrol-inspired pattern drilling operations
  'drill-line': {
    name: 'Drill Line Pattern',
    description: 'Drill a line of equally spaced holes',
    fields: [
      { id: 'x', label: 'Start X (in)', default: 0 },
      { id: 'y', label: 'Start Y (in)', default: 0 },
      { id: 'depth', label: 'Hole Depth (in)', default: 0.750 },
      { id: 'numHoles', label: 'Number of Holes', default: 4 },
      { id: 'spacing', label: 'Spacing (in)', default: 1.0 },
      { id: 'angle', label: 'Line Angle (deg)', default: 0 },
      { id: 'retract', label: 'Retract R (in)', default: 0.100 },
    ]
  },
  'drill-circle': {
    name: 'Drill Circle Pattern (Bolt Circle)',
    description: 'Drill holes in a circular pattern',
    fields: [
      { id: 'centerX', label: 'Center X (in)', default: 0 },
      { id: 'centerY', label: 'Center Y (in)', default: 0 },
      { id: 'depth', label: 'Hole Depth (in)', default: 0.750 },
      { id: 'numHoles', label: 'Number of Holes', default: 6 },
      { id: 'radius', label: 'Bolt Circle Radius (in)', default: 1.5 },
      { id: 'startAngle', label: 'Start Angle (deg)', default: 0 },
      { id: 'retract', label: 'Retract R (in)', default: 0.100 },
    ]
  },
  'drill-arc': {
    name: 'Drill Arc Pattern',
    description: 'Drill holes along an arc',
    fields: [
      { id: 'centerX', label: 'Center X (in)', default: 0 },
      { id: 'centerY', label: 'Center Y (in)', default: 0 },
      { id: 'depth', label: 'Hole Depth (in)', default: 0.750 },
      { id: 'numHoles', label: 'Number of Holes', default: 5 },
      { id: 'radius', label: 'Arc Radius (in)', default: 2.0 },
      { id: 'startAngle', label: 'Start Angle (deg)', default: 0 },
      { id: 'arcSpan', label: 'Arc Span (deg)', default: 90 },
      { id: 'retract', label: 'Retract R (in)', default: 0.100 },
    ]
  },
};

// Generate turning G-code
export const generateTurningCode = (operation, params, settings, controller = 'fanuc') => {
  const cfg = controllerConfig[controller];
  const lines = [];

  const { 
    programNum = 1, 
    programName = '',
    tool = 1, 
    offset = 1,
    spindleMode = 'G97',
    speed = 1200,
    roughFeed = 0.2,
    finishFeed = 0.1,
    safeZ = 10,
    safeX = 100,
    addHeader = true,
  } = settings;

  // Program header
  lines.push(cfg.programFormat(programNum, programName));
  
  if (addHeader) {
    lines.push(cfg.comment(`${operation.toUpperCase()} OPERATION`));
    lines.push(cfg.comment(`Generated by G-Code Pro`));
    lines.push(cfg.comment(`Date: ${new Date().toLocaleDateString()}`));
    lines.push('');
  }

  // For bar-puller, handle separately as it has its own complete sequence
  if (operation === 'bar-puller') {
    lines.push(cfg.comment('BAR PULLER OPERATION'));
    lines.push(cfg.comment('1" SQ SHANK HEAVY DUTY BAR PULLER'));
    if (params.gripRange === 'extra') {
      lines.push(cfg.comment('EXTRA CAPACITY JAW: 1-7/8" - 3-3/4" GRIP'));
    } else {
      lines.push(cfg.comment('STANDARD JAW: 1/8" - 2-1/4" GRIP'));
    }
    lines.push('');
    lines.push(cfg.homeReturn);
    lines.push(`T${String(tool).padStart(2, '0')}${String(tool).padStart(2, '0')} ${cfg.comment('BAR PULLER')}`);
    lines.push(cfg.spindleOff);
    lines.push(cfg.feedPerMin);
    lines.push(`${cfg.rapid} X${params.safeX || 4.0} Z0.0 ${cfg.comment('RAPID TO CENTER, SAFE Z')}`);
    lines.push(`${cfg.linear} X0.0 F${params.approachFeed || 50.0} ${cfg.comment('MOVE TO PULL POSITION')}`);
    lines.push(`M11 ${cfg.comment('UNCLAMP CHUCK')}`);
    lines.push(`G04 X${params.dwellTime || 1.0} ${cfg.comment('DWELL TO ENSURE UNCLAMP')}`);
    lines.push(`${cfg.linear} W${params.pullLength || 1.0} F${params.pullFeed || 20.0} ${cfg.comment('PULL BAR OUT')}`);
    lines.push(`M10 ${cfg.comment('CLAMP CHUCK')}`);
    lines.push(`G04 X${params.dwellTime || 1.0} ${cfg.comment('DWELL TO ENSURE CLAMP')}`);
    lines.push(`${cfg.rapid} X${params.safeX || 4.0} ${cfg.comment('MOVE OFF PART')}`);
    lines.push(`${cfg.rapid} Z1.0 ${cfg.comment('BACK AWAY FROM PART')}`);
    lines.push(cfg.feedPerRev);
    lines.push('');
    
    // Program end
    if (addHeader) {
      lines.push(cfg.homeReturn);
      lines.push(cfg.programEnd);
    }
    
    return lines;
  }

  // Safe start (for non-bar-puller operations)
  cfg.safeStart.forEach(line => lines.push(line));
  lines.push('');

  // Tool call
  lines.push(cfg.toolCall(tool, offset));
  lines.push(cfg.spindleOn(spindleMode, speed));
  lines.push(cfg.coolantOn);
  lines.push(cfg.feedPerRev);
  lines.push('');

  // Safe position
  lines.push(`${cfg.rapid} X${safeX} Z${safeZ}`);
  lines.push('');

  // Operation-specific code
  switch (operation) {
    case 'facing':
      // G72 Facing Cycle (Rough) + G70 Finish
      lines.push(cfg.comment('FACING CYCLE - G72'));
      if (supportsFanucCycles(controller)) {
        const facingDoc = params.doc || 0.100;  // Depth per pass
        const facingAllowance = params.finishAllowance || 0.010;
        
        lines.push(`G72 W${facingDoc} R0.030 ${cfg.comment('FACING ROUGH - DOC=${facingDoc}"')}`);
        lines.push(`G72 P100 Q110 U${facingAllowance} W${facingAllowance / 2} F${roughFeed}`);
        lines.push(`N100 G00 Z0.005`);
        lines.push(`G01 X${params.stockDia + 0.1} F${roughFeed} ${cfg.comment('START OD')}`);
        lines.push(`N110 X${params.finishDia || -0.050} ${cfg.comment('FACE TO CENTER/ID')}`);
        lines.push('');
        lines.push(cfg.comment('FACING FINISH PASS - G70'));
        lines.push('G70 P100 Q110');
      } else {
        // Long-hand for non-FANUC
        lines.push(`${cfg.rapid} X${params.stockDia + 2} Z0`);
        lines.push(`${cfg.linear} X${params.finishDia || -2} F${roughFeed}`);
        lines.push(`${cfg.rapid} Z${safeZ}`);
      }
      break;

    case 'od-roughing':
      // G71 OD Roughing Cycle + G70 Finish
      lines.push(cfg.comment('OD ROUGHING CYCLE - G71'));
      if (supportsFanucCycles(controller)) {
        const roughDoc = params.doc || 0.100;
        const xAllowance = params.finishAllowance || 0.020;
        const zAllowance = (params.finishAllowance || 0.020) / 2;
        
        lines.push(`G71 U${roughDoc} R0.030 ${cfg.comment('ROUGH DOC=${roughDoc}" RETRACT=0.030')}`);
        lines.push(`G71 P100 Q110 U${xAllowance} W${zAllowance} F${roughFeed} ${cfg.comment('FINISH ALLOW X=${xAllowance} Z=${zAllowance}')}`);
        lines.push(`N100 G00 X${params.finishDia}`);
        lines.push(`G01 Z0 F${finishFeed}`);
        lines.push(`Z-${params.length}`);
        lines.push(`N110 X${params.stockDia}`);
        lines.push('');
        lines.push(cfg.comment('OD FINISH PASS - G70'));
        lines.push('G70 P100 Q110');
      } else {
        // Long-hand multi-pass for non-FANUC
        lines.push(`${cfg.rapid} X${params.stockDia + 2} Z2`);
        const roughDoc = params.doc || 0.100;
        let currentDia = params.stockDia;
        while (currentDia > params.finishDia) {
          currentDia = Math.max(currentDia - roughDoc * 2, params.finishDia);
          lines.push(`${cfg.linear} X${currentDia.toFixed(4)} F${roughFeed}`);
          lines.push(`Z-${params.length}`);
          lines.push(`${cfg.rapid} X${(currentDia + 2).toFixed(4)}`);
          lines.push(`Z2`);
        }
      }
      break;

    case 'od-finishing':
      lines.push(cfg.comment('OD FINISHING PASS'));
      lines.push(`${cfg.rapid} X${(params.finishDia + 0.050).toFixed(4)} Z0.100`);
      lines.push(`${cfg.linear} X${params.finishDia} F${finishFeed} ${cfg.comment('APPROACH')}`);
      lines.push(`Z0`);
      lines.push(`Z-${params.length} F${finishFeed}`);
      lines.push(`${cfg.rapid} X${(params.finishDia + 1.0).toFixed(4)}`);
      break;

    case 'od-threading':
      // Long-hand multi-pass OD threading (NO canned cycle)
      {
        const majorDia = parseFloat(params.majorDia) || 0.500;
        const minorDia = parseFloat(params.minorDia) || (majorDia - 0.0920);
        const threadPitch = parseFloat(params.pitch) || 0.07692;
        const threadLength = parseFloat(params.length) || 1.0;
        const threadMethod = params.threadMethod || 'compound';
        const springPasses = parseInt(params.springPasses) || 2;
        const chamferEntry = parseFloat(params.chamferEntry) || 1;
        const tpi = parseFloat(params.tpi) || Math.round(1 / threadPitch);
        const threadDesig = params.threadDesignation || `${tpi} TPI`;
        
        // Calculate total thread depth (single depth)
        const totalDepth = (majorDia - minorDia) / 2;
        
        // Lead-in/lead-out distances
        const leadIn = threadPitch * chamferEntry;
        const startZ = leadIn;
        const endZ = -(threadLength + leadIn);
        
        lines.push(cfg.comment(`THREADING OPERATION - ${threadDesig}`));
        lines.push(cfg.comment(`MAJOR DIA: ${majorDia.toFixed(4)}" MINOR DIA: ${minorDia.toFixed(4)}"`));
        lines.push(cfg.comment(`PITCH: ${threadPitch.toFixed(5)}" (${tpi} TPI)`));
        lines.push(cfg.comment(`TOTAL DEPTH: ${totalDepth.toFixed(5)}" PER SIDE`));
        lines.push(cfg.comment(`METHOD: ${threadMethod === 'compound' ? '29.5 DEG COMPOUND ANGLE' : 'STRAIGHT INFEED'}`));
        lines.push('');
        
        // Generate passes using square-root method for equal chip load
        const numPasses = Math.max(4, Math.ceil(totalDepth / 0.005));
        
        // Calculate all passes
        const passes = [];
        for (let i = 1; i <= numPasses; i++) {
          const cumulativeDepth = totalDepth * Math.sqrt(i / numPasses);
          passes.push(parseFloat(cumulativeDepth.toFixed(5)));
        }
        
        // Use G97 constant RPM for threading
        const threadRPM = params.threadRPM || Math.min(1200, Math.round(speed * 0.6));
        lines.push(`G97 S${threadRPM} M03 ${cfg.comment('CONSTANT RPM FOR THREADING')}`);
        lines.push('');
        
        // Cutting passes
        lines.push(cfg.comment(`--- ${numPasses} CUTTING PASSES (SQUARE ROOT METHOD) ---`));
        
        for (let i = 0; i < passes.length; i++) {
          const passDepth = passes[i];
          let xCutDia;
          
          if (threadMethod === 'compound') {
            // 29.5-degree compound angle infeed
            // X component = depth * cos(29.5) * 2 (diameter)
            // Z component = depth * sin(29.5) (adds slight Z shift per pass)
            const xComponent = passDepth * Math.cos(29.5 * Math.PI / 180);
            xCutDia = majorDia - (xComponent * 2);
            const zShift = passDepth * Math.sin(29.5 * Math.PI / 180);
            
            lines.push(cfg.comment(`PASS ${i + 1} - DEPTH: ${passDepth.toFixed(5)}" (COMPOUND)`));
            lines.push(`${cfg.rapid} X${majorDia.toFixed(4)} Z${(startZ + zShift).toFixed(4)}`);
            lines.push(`${cfg.rapid} X${xCutDia.toFixed(4)}`);
            lines.push(`G32 Z${endZ.toFixed(4)} F${threadPitch.toFixed(5)} ${cfg.comment('THREAD CUT')}`);
            lines.push(`${cfg.rapid} X${(majorDia + 0.050).toFixed(4)} ${cfg.comment('RETRACT')}`);
            lines.push(`${cfg.rapid} Z${(startZ + zShift).toFixed(4)}`);
          } else {
            // Straight infeed
            xCutDia = majorDia - (passDepth * 2);
            
            lines.push(cfg.comment(`PASS ${i + 1} - DEPTH: ${passDepth.toFixed(5)}" (STRAIGHT)`));
            lines.push(`${cfg.rapid} X${majorDia.toFixed(4)} Z${startZ.toFixed(4)}`);
            lines.push(`${cfg.rapid} X${xCutDia.toFixed(4)}`);
            lines.push(`G32 Z${endZ.toFixed(4)} F${threadPitch.toFixed(5)} ${cfg.comment('THREAD CUT')}`);
            lines.push(`${cfg.rapid} X${(majorDia + 0.050).toFixed(4)} ${cfg.comment('RETRACT')}`);
            lines.push(`${cfg.rapid} Z${startZ.toFixed(4)}`);
          }
          lines.push('');
        }
        
        // Spring passes (at final depth, no additional cutting)
        if (springPasses > 0) {
          const finalX = majorDia - (passes[passes.length - 1] * 2);
          lines.push(cfg.comment(`--- ${springPasses} SPRING PASSES AT FINAL DEPTH ---`));
          for (let s = 0; s < springPasses; s++) {
            lines.push(cfg.comment(`SPRING PASS ${s + 1}`));
            lines.push(`${cfg.rapid} X${majorDia.toFixed(4)} Z${startZ.toFixed(4)}`);
            lines.push(`${cfg.rapid} X${finalX.toFixed(4)}`);
            lines.push(`G32 Z${endZ.toFixed(4)} F${threadPitch.toFixed(5)}`);
            lines.push(`${cfg.rapid} X${(majorDia + 0.050).toFixed(4)}`);
            lines.push(`${cfg.rapid} Z${startZ.toFixed(4)}`);
          }
          lines.push('');
        }
        
        // Return to CSS mode
        lines.push(`G96 S${speed} M03 ${cfg.comment('RETURN TO CSS MODE')}`);
      }
      break;

    case 'id-boring':
      // G71 for ID Boring (with negative U/W allowances)
      lines.push(cfg.comment('ID BORING CYCLE - G71'));
      if (supportsFanucCycles(controller)) {
        const boreDoc = params.doc || 0.080;
        const xAllowance = -(params.finishAllowance || 0.015); // Negative for ID
        const zAllowance = (params.finishAllowance || 0.015) / 2;
        
        lines.push(`G71 U${boreDoc} R0.020 ${cfg.comment('BORE DOC=${boreDoc}"')}`);
        lines.push(`G71 P200 Q210 U${xAllowance} W${zAllowance} F${roughFeed} ${cfg.comment('NEG X ALLOW FOR ID')}`);
        lines.push(`N200 G00 X${params.finishDia}`);
        lines.push(`G01 Z0 F${finishFeed}`);
        lines.push(`Z-${params.depth}`);
        lines.push(`N210 X${params.startDia || (params.finishDia - 0.5)}`);
        lines.push('');
        lines.push(cfg.comment('BORE FINISH PASS - G70'));
        lines.push('G70 P200 Q210');
      } else {
        // Long-hand for non-FANUC
        lines.push(`${cfg.rapid} X${(params.startDia - 0.1).toFixed(4)} Z0.100`);
        const boreDoc = params.doc || 0.080;
        let boreDia = params.startDia;
        while (boreDia < params.finishDia) {
          boreDia = Math.min(boreDia + boreDoc * 2, params.finishDia);
          lines.push(`${cfg.linear} X${boreDia.toFixed(4)} F${roughFeed}`);
          lines.push(`Z-${params.depth}`);
          lines.push(`${cfg.rapid} X${(boreDia - 0.1).toFixed(4)}`);
          lines.push(`Z0.100`);
        }
      }
      break;

    case 'grooving':
      // G75 Grooving Cycle
      lines.push(cfg.comment('GROOVING CYCLE - G75'));
      if (supportsFanucCycles(controller)) {
        const grooveWidth = params.grooveWidth || 0.125;
        const pecks = Math.ceil((params.stockDia - params.grooveDia) / 2 / 0.050);
        const peckDepth = Math.round(0.050 * 10000); // 0.050" peck in 0.0001 units
        
        lines.push(`${cfg.rapid} X${(params.stockDia + 0.100).toFixed(4)} Z${params.zPosition}`);
        lines.push(`G75 R0.010 ${cfg.comment('RETRACT PER PECK')}`);
        lines.push(`G75 X${params.grooveDia} Z${(params.zPosition - grooveWidth + 0.010).toFixed(4)} P${peckDepth} Q${Math.round(grooveWidth * 10000)} F${roughFeed / 2}`);
      } else {
        lines.push(`${cfg.rapid} X${params.stockDia + 0.100} Z${params.zPosition}`);
        lines.push(`${cfg.linear} X${params.grooveDia} F${roughFeed / 2}`);
        lines.push(`G04 X0.5 ${cfg.comment('DWELL')}`);
        lines.push(`${cfg.rapid} X${params.stockDia + 0.100}`);
      }
      break;

    case 'parting':
      // G75 Parting Cycle
      lines.push(cfg.comment('PARTING CYCLE - G75'));
      if (supportsFanucCycles(controller)) {
        const partDia = params.partDia || 0;
        const peckDepth = Math.round(0.030 * 10000); // 0.030" peck
        
        lines.push(`${cfg.rapid} X${(safeX + 0.050).toFixed(4)} Z${params.zPosition}`);
        lines.push(`G75 R0.005 ${cfg.comment('SMALL RETRACT FOR CHIP BREAK')}`);
        lines.push(`G75 X${partDia} Z${params.zPosition} P${peckDepth} Q0 F${roughFeed / 3} ${cfg.comment('PECK PARTING')}`);
      } else {
        lines.push(`${cfg.rapid} X${safeX} Z${params.zPosition}`);
        lines.push(`${cfg.linear} X${params.partDia || 0} F${roughFeed / 3}`);
        lines.push(`${cfg.rapid} X${safeX}`);
      }
      break;

    case 'bar-puller':
      // Bar Puller Operation - 1" Square Shank Heavy Duty Bar Puller
      // Standard Jaw: 1/8" - 2-1/4" Gripping Range
      // Extra Capacity Jaw: 1-7/8" - 3-3/4" Gripping Range
      // 3-7/8" Wide
      lines.push(cfg.comment('BAR PULLER OPERATION'));
      lines.push(cfg.comment('1" SQ SHANK HEAVY DUTY BAR PULLER'));
      lines.push(cfg.homeReturn);
      lines.push(`T${String(tool).padStart(2, '0')}${String(tool).padStart(2, '0')} ${cfg.comment('BAR PULLER')}`);
      lines.push(cfg.spindleOff);
      lines.push(cfg.feedPerMin);
      lines.push(`${cfg.rapid} X${params.safeX || 4.0} Z0.0 ${cfg.comment('RAPID TO CENTER, SAFE Z')}`);
      lines.push(`${cfg.linear} X0.0 F${params.approachFeed || 50.0} ${cfg.comment('MOVE TO PULL POSITION')}`);
      lines.push(`M11 ${cfg.comment('UNCLAMP CHUCK')}`);
      lines.push(`G04 X${params.dwellTime || 1.0} ${cfg.comment('DWELL TO ENSURE UNCLAMP')}`);
      lines.push(`${cfg.linear} W${params.pullLength || 1.0} F${params.pullFeed || 20.0} ${cfg.comment('PULL BAR OUT')}`);
      lines.push(`M10 ${cfg.comment('CLAMP CHUCK')}`);
      lines.push(`G04 X${params.dwellTime || 1.0} ${cfg.comment('DWELL TO ENSURE CLAMP')}`);
      lines.push(`${cfg.rapid} X${params.safeX || 4.0} ${cfg.comment('MOVE OFF PART')}`);
      lines.push(`${cfg.rapid} Z1.0 ${cfg.comment('BACK AWAY FROM PART')}`);
      lines.push(cfg.feedPerRev);
      break;

    case 'center-drill':
      // G81 Spotting/Center Drill Cycle
      lines.push(cfg.comment('CENTER DRILL - SPOT HOLE'));
      lines.push(`${cfg.rapid} X0 Z0.100`);
      if (supportsFanucCycles(controller)) {
        lines.push(`G81 Z-${params.depth || 0.150} R0.100 F${(roughFeed * 0.5).toFixed(3)} ${cfg.comment('SPOTTING CYCLE')}`);
        lines.push('G80 ${cfg.comment(\'CANCEL CANNED CYCLE\')}');
      } else {
        lines.push(`${cfg.linear} Z-${params.depth || 0.150} F${roughFeed * 0.5}`);
        lines.push(`${cfg.rapid} Z0.100`);
      }
      lines.push(cfg.comment(`SPOT DIA: ${params.drillDia}" @ ${params.spotAngle || 90} DEG`));
      break;

    case 'drilling':
      // G74 or G83 Peck Drilling Cycle
      lines.push(cfg.comment('PECK DRILLING CYCLE - G74'));
      if (params.drillType === 'pilot') {
        lines.push(cfg.comment('PILOT DRILL FOR BORE PREP'));
      }
      lines.push(cfg.comment(`DRILL DIA: ${params.drillDia}"`));
      lines.push(`${cfg.rapid} X0 Z0.100`);
      if (supportsFanucCycles(controller)) {
        const peckDepth = Math.round((params.peck || 0.200) * 10000); // In 0.0001" units
        // G74 R(retract); G74 Z(depth) Q(peck) F(feed)
        lines.push(`G74 R0.030 ${cfg.comment('RETRACT PER PECK')}`);
        lines.push(`G74 Z-${params.depth} Q${peckDepth} F${roughFeed} ${cfg.comment('PECK=${params.peck}"')}`);
        lines.push(`G80 ${cfg.comment('CANCEL CANNED CYCLE')}`);
      } else {
        // Long-hand peck drilling
        const peckDepth = params.peck || 0.200;
        let currentZ = 0;
        while (currentZ > -params.depth) {
          currentZ = Math.max(currentZ - peckDepth, -params.depth);
          lines.push(`${cfg.linear} Z${currentZ.toFixed(4)} F${roughFeed}`);
          lines.push(`${cfg.rapid} Z0.100`);
          lines.push(`${cfg.rapid} Z${(currentZ + 0.050).toFixed(4)}`);
        }
      }
      break;

    case 'taper':
      lines.push(cfg.comment('TAPER TURNING'));
      if (supportsFanucCycles(controller)) {
        // G71 Roughing Cycle with taper profile
        const taperDoc = params.doc || 0.100;
        const taperXAllow = params.finishAllowance || 0.020;
        const taperZAllow = (params.finishAllowance || 0.020) / 2;
        
        lines.push(cfg.comment('TAPER ROUGHING - G71'));
        lines.push(`G71 U${taperDoc} R0.030`);
        lines.push(`G71 P100 Q110 U${taperXAllow} W${taperZAllow} F${roughFeed}`);
        lines.push(`N100 G00 X${params.endDia}`);
        lines.push(`G01 Z0 F${finishFeed}`);
        lines.push(`X${params.startDia} Z-${params.length}`);
        lines.push(`N110 X${(params.startDia + 0.5).toFixed(4)}`);
        lines.push('');
        lines.push(cfg.comment('TAPER FINISH PASS - G70'));
        lines.push('G70 P100 Q110');
      } else {
        lines.push(`${cfg.rapid} X${params.startDia + 2} Z2`);
        lines.push(`${cfg.linear} X${params.startDia} Z0 F${roughFeed}`);
        lines.push(`X${params.endDia} Z-${params.length}`);
        lines.push(`${cfg.rapid} X${params.startDia + 10}`);
      }
      break;

    case 'pattern-repeat':
      // G73 Pattern Repeating Cycle - for near-net-shape castings/forgings
      lines.push(cfg.comment('G73 PATTERN REPEATING CYCLE'));
      lines.push(cfg.comment('FOR CASTINGS/FORGINGS WITH NEAR-NET-SHAPE STOCK'));
      if (supportsFanucCycles(controller)) {
        const stockAllow = params.stockAllowance || 0.120;
        const depthPerPass = params.doc || 0.040;
        const numDivisions = Math.ceil(stockAllow / depthPerPass);
        const xFinishAllow = params.finishAllowance || 0.015;
        const zFinishAllow = (params.finishAllowance || 0.015) / 2;
        const profileType = params.profileType || 'straight';
        
        lines.push(cfg.comment(`STOCK ALLOWANCE: ${stockAllow}" / ${numDivisions} PASSES`));
        lines.push(`G73 U${stockAllow} W${(stockAllow / 2).toFixed(4)} R${numDivisions}`);
        lines.push(`G73 P100 Q110 U${xFinishAllow} W${zFinishAllow} F${roughFeed}`);
        
        // Define finish profile based on type
        if (profileType === 'taper') {
          lines.push(`N100 G00 X${params.endDia || params.finishDia}`);
          lines.push(`G01 Z0 F${finishFeed}`);
          lines.push(`X${params.finishDia} Z-${params.length}`);
          lines.push(`N110 X${(params.finishDia + 0.5).toFixed(4)}`);
        } else if (profileType === 'contour') {
          lines.push(`N100 G00 X${params.finishDia}`);
          lines.push(`G01 Z0 F${finishFeed}`);
          lines.push(`Z-${(params.length * 0.3).toFixed(4)} ${cfg.comment('FIRST STEP')}`);
          lines.push(`X${(params.finishDia + 0.25).toFixed(4)}`);
          lines.push(`Z-${(params.length * 0.7).toFixed(4)} ${cfg.comment('SECOND STEP')}`);
          lines.push(`X${(params.finishDia + 0.5).toFixed(4)}`);
          lines.push(`Z-${params.length}`);
          lines.push(`N110 X${(params.finishDia + 1.0).toFixed(4)}`);
        } else {
          // Straight profile
          lines.push(`N100 G00 X${params.finishDia}`);
          lines.push(`G01 Z0 F${finishFeed}`);
          lines.push(`Z-${params.length}`);
          lines.push(`N110 X${(params.finishDia + 0.5).toFixed(4)}`);
        }
        
        lines.push('');
        lines.push(cfg.comment('G73 FINISH PASS - G70'));
        lines.push('G70 P100 Q110');
      } else {
        // Long-hand fallback for non-FANUC
        lines.push(cfg.comment('MANUAL PATTERN REPEAT (NO G73)'));
        const stockAllowLH = params.stockAllowance || 0.120;
        const docLH = params.doc || 0.040;
        const passesLH = Math.ceil(stockAllowLH / docLH);
        for (let p = 1; p <= passesLH; p++) {
          const offset = stockAllowLH - (docLH * p);
          const cutDia = params.finishDia + (offset > 0 ? offset * 2 : 0);
          lines.push(cfg.comment(`PASS ${p}/${passesLH}`));
          lines.push(`${cfg.rapid} X${(cutDia + 0.1).toFixed(4)} Z0.1`);
          lines.push(`${cfg.linear} X${cutDia.toFixed(4)} Z0 F${roughFeed}`);
          lines.push(`Z-${params.length}`);
          lines.push(`${cfg.rapid} X${(cutDia + 1.0).toFixed(4)}`);
          lines.push(`Z0.1`);
        }
      }
      break;

    default:
      lines.push(cfg.comment('CUSTOM OPERATION'));
  }

  lines.push('');
  
  // End program
  lines.push(cfg.coolantOff);
  lines.push(cfg.spindleOff);
  lines.push(cfg.homeReturn);
  lines.push(cfg.programEnd);

  return lines;
};

// Generate milling G-code
export const generateMillingCode = (operation, params, settings, controller = 'fanuc') => {
  const cfg = controllerConfig[controller];
  const lines = [];

  const {
    programNum = 1,
    programName = '',
    tool = 1,
    speed = 2000,
    feed = 300,
    safeZ = 50,
    addHeader = true,
  } = settings;

  // Program header
  lines.push(cfg.programFormat(programNum, programName));
  
  if (addHeader) {
    lines.push(cfg.comment(`${operation.toUpperCase()} OPERATION`));
    lines.push(cfg.comment(`Generated by G-Code Pro`));
    lines.push('');
  }

  // Safe start
  if (controller !== 'heidenhain') {
    lines.push('G17 G40 G80');
    lines.push('G21');
    lines.push('G90');
  }
  lines.push('');

  // Tool call
  lines.push(cfg.toolCall(tool, tool));
  lines.push(cfg.spindleOn('G97', speed));
  lines.push(cfg.coolantOn);
  lines.push('');

  // Safe position
  lines.push(`${cfg.rapid} Z${safeZ}`);
  lines.push('');

  // Operation-specific code
  switch (operation) {
    case 'face-mill':
      lines.push(cfg.comment('FACE MILLING'));
      const stepover = params.toolDia * (params.stepover / 100);
      const passes = Math.ceil(params.length / stepover);
      lines.push(`${cfg.rapid} X-${params.toolDia / 2} Y-${stepover / 2}`);
      lines.push(`${cfg.rapid} Z2`);
      lines.push(`${cfg.linear} Z-${params.depth} F${feed / 3}`);
      for (let i = 0; i < passes; i++) {
        const y = i * stepover;
        if (i % 2 === 0) {
          lines.push(`${cfg.linear} X${params.width + params.toolDia / 2} Y${y} F${feed}`);
        } else {
          lines.push(`${cfg.linear} X-${params.toolDia / 2} Y${y} F${feed}`);
        }
      }
      lines.push(`${cfg.rapid} Z${safeZ}`);
      break;

    case 'pocket-rect':
      lines.push(cfg.comment('RECTANGULAR POCKET'));
      const so = params.toolDia * 0.5;
      const numZ = Math.ceil(params.depth / params.stepdown);
      lines.push(`${cfg.rapid} X${so} Y${so}`);
      lines.push(`${cfg.rapid} Z5`);
      for (let z = 1; z <= numZ; z++) {
        const zDepth = Math.min(z * params.stepdown, params.depth);
        lines.push(`${cfg.linear} Z-${zDepth} F${feed / 3}`);
        lines.push(`${cfg.linear} X${params.width - so} F${feed}`);
        lines.push(`Y${params.length - so}`);
        lines.push(`X${so}`);
        lines.push(`Y${so}`);
      }
      lines.push(`${cfg.rapid} Z${safeZ}`);
      break;

    case 'drill':
    case 'peck-drill':
      lines.push(cfg.comment(operation === 'peck-drill' ? 'PECK DRILLING' : 'DRILLING'));
      lines.push(`${cfg.rapid} X${params.x} Y${params.y}`);
      lines.push(`${cfg.rapid} Z${params.retract}`);
      if (operation === 'peck-drill') {
        lines.push(`G83 Z-${params.depth} Q${params.peck} R${params.retract} F${feed}`);
      } else {
        lines.push(`G81 Z-${params.depth} R${params.retract} F${feed}`);
      }
      lines.push('G80');
      break;

    case 'tap':
      lines.push(cfg.comment('TAPPING'));
      const tapFeed = speed * params.pitch;
      lines.push(`${cfg.rapid} X${params.x} Y${params.y}`);
      lines.push(`${cfg.rapid} Z2`);
      lines.push(`G84 Z-${params.depth} R2 F${tapFeed}`);
      lines.push('G80');
      break;

    // Javatrol-inspired pattern drilling
    case 'drill-line': {
      lines.push(cfg.comment('DRILLING LINE PATTERN'));
      const theta = (params.angle || 0) * (Math.PI / 180);
      const startX = parseFloat(params.x);
      const startY = parseFloat(params.y);
      const drillSafeZ = parseFloat(params.retract) + 2;
      const finalZ = -parseFloat(params.depth);
      
      // Position to first hole
      lines.push(`${cfg.rapid} X${startX.toFixed(4)} Y${startY.toFixed(4)}`);
      lines.push(`G43 H${tool} Z${drillSafeZ}`);
      lines.push('G98 G81 Z' + finalZ.toFixed(4) + ' R' + params.retract + ' F' + feed);
      
      // Generate remaining holes along the line
      for (let i = 1; i < params.numHoles; i++) {
        const holeX = startX + (params.spacing * Math.cos(theta) * i);
        const holeY = startY + (params.spacing * Math.sin(theta) * i);
        lines.push(`X${holeX.toFixed(4)} Y${holeY.toFixed(4)}`);
      }
      lines.push('G80');
      break;
    }

    case 'drill-circle': {
      lines.push(cfg.comment('DRILLING CIRCLE PATTERN (BOLT CIRCLE)'));
      const centerX = parseFloat(params.centerX);
      const centerY = parseFloat(params.centerY);
      const boltRadius = parseFloat(params.radius);
      const startDeg = parseFloat(params.startAngle);
      const pitchAngle = 360 / params.numHoles;
      const drillSafeZ = parseFloat(params.retract) + 2;
      const finalZ = -parseFloat(params.depth);
      
      // Calculate first hole position
      const firstAngleRad = startDeg * (Math.PI / 180);
      const firstX = centerX + boltRadius * Math.cos(firstAngleRad);
      const firstY = centerY + boltRadius * Math.sin(firstAngleRad);
      
      lines.push(`${cfg.rapid} X${firstX.toFixed(4)} Y${firstY.toFixed(4)}`);
      lines.push(`G43 H${tool} Z${drillSafeZ}`);
      lines.push('G98 G81 Z' + finalZ.toFixed(4) + ' R' + params.retract + ' F' + feed);
      
      // Generate remaining holes around circle
      for (let i = 1; i < params.numHoles; i++) {
        const angleRad = (startDeg + pitchAngle * i) * (Math.PI / 180);
        const holeX = centerX + boltRadius * Math.cos(angleRad);
        const holeY = centerY + boltRadius * Math.sin(angleRad);
        lines.push(`X${holeX.toFixed(4)} Y${holeY.toFixed(4)}`);
      }
      lines.push('G80');
      break;
    }

    case 'drill-arc': {
      lines.push(cfg.comment('DRILLING ARC PATTERN'));
      const centerX = parseFloat(params.centerX);
      const centerY = parseFloat(params.centerY);
      const arcRadius = parseFloat(params.radius);
      const startDeg = parseFloat(params.startAngle);
      const arcSpan = parseFloat(params.arcSpan);
      const pitchAngle = params.numHoles > 1 ? arcSpan / (params.numHoles - 1) : 0;
      const drillSafeZ = parseFloat(params.retract) + 2;
      const finalZ = -parseFloat(params.depth);
      
      // Calculate first hole
      const firstAngleRad = startDeg * (Math.PI / 180);
      const firstX = centerX + arcRadius * Math.cos(firstAngleRad);
      const firstY = centerY + arcRadius * Math.sin(firstAngleRad);
      
      lines.push(`${cfg.rapid} X${firstX.toFixed(4)} Y${firstY.toFixed(4)}`);
      lines.push(`G43 H${tool} Z${drillSafeZ}`);
      lines.push('G98 G81 Z' + finalZ.toFixed(4) + ' R' + params.retract + ' F' + feed);
      
      // Generate holes along arc
      for (let i = 1; i < params.numHoles; i++) {
        const angleRad = (startDeg + pitchAngle * i) * (Math.PI / 180);
        const holeX = centerX + arcRadius * Math.cos(angleRad);
        const holeY = centerY + arcRadius * Math.sin(angleRad);
        lines.push(`X${holeX.toFixed(4)} Y${holeY.toFixed(4)}`);
      }
      lines.push('G80');
      break;
    }

    case 'pocket-circ': {
      lines.push(cfg.comment('CIRCULAR POCKET - HELICAL RAMP'));
      const pocketRadius = params.diameter / 2;
      const toolR = params.toolDia / 2;
      const centerX = 0;
      const centerY = 0;
      const pocketSafeZ = 2;
      const numPasses = Math.ceil(params.depth / params.stepdown);
      
      lines.push(`${cfg.rapid} X${centerX} Y${centerY}`);
      lines.push(`${cfg.rapid} Z${pocketSafeZ}`);
      
      // Rough passes with helical ramp-down
      for (let pass = 1; pass <= numPasses; pass++) {
        const currentZ = -Math.min(pass * params.stepdown, params.depth);
        const startR = toolR + 0.1;  // Start just outside tool center
        
        // Move to start position
        lines.push(`${cfg.linear} G41 D${tool} X${(centerX + startR).toFixed(4)}`);
        // Helical ramp down
        lines.push(`G03 I-${startR.toFixed(4)} J0 Z${currentZ.toFixed(4)} F${feed}`);
        // Spiral outward to open pocket
        let currentR = startR;
        const stepOver = toolR * 0.7;  // 70% stepover
        while (currentR + stepOver < pocketRadius) {
          currentR += stepOver;
          lines.push(`${cfg.linear} X${(centerX + currentR).toFixed(4)}`);
          lines.push(`G03 I-${currentR.toFixed(4)} J0`);
        }
        // Final pass at full radius
        if (currentR < pocketRadius) {
          lines.push(`${cfg.linear} X${(centerX + pocketRadius - toolR).toFixed(4)}`);
          lines.push(`G03 I-${(pocketRadius - toolR).toFixed(4)} J0`);
        }
        lines.push(`${cfg.linear} G40 X${centerX}`);
      }
      lines.push(`${cfg.rapid} Z${safeZ}`);
      break;
    }

    default:
      lines.push(cfg.comment('CUSTOM MILLING OPERATION'));
  }

  lines.push('');
  lines.push(cfg.coolantOff);
  lines.push(cfg.spindleOff);
  lines.push(`${cfg.rapid} Z${safeZ}`);
  lines.push(cfg.homeReturn);
  lines.push(cfg.programEnd);

  return lines;
};

// Format G-code line for display with syntax highlighting
export const formatGCodeLine = (line) => {
  if (!line) return '';
  
  return line
    .replace(/(G\d+\.?\d*)/g, '<span class="gcode-gcode">$1</span>')
    .replace(/(M\d+)/g, '<span class="gcode-mcode">$1</span>')
    .replace(/([XYZUVWPQRSF]-?\d+\.?\d*)/g, '<span class="gcode-coord">$1</span>')
    .replace(/(T\d+)/g, '<span class="gcode-tool">$1</span>')
    .replace(/(\([^)]*\))/g, '<span class="gcode-comment">$1</span>')
    .replace(/(;.*)/g, '<span class="gcode-comment">$1</span>');
};
